import type { ReactElement } from 'react';
import { genreName, useGamesSite } from '../context';
import { isNewGame } from '../format';
import { gamePagePath } from '../routes';
import type { GameCard } from '../types';
import { GameIcon, type GameIconSize } from './GameIcon';
import { PlayButton } from './PlayButton';

/** "Word · Flappyland" for a game inside an arche; "Word" for an arche of its own. */
export function useGameSubtitle(game: GameCard): string {
  const { genreLabels } = useGamesSite();
  const genre = game.genres[0] ? genreName(genreLabels, game.genres[0]) : 'Game';
  return game.kind === 'listing' ? `${genre} · ${game.archeName}` : genre;
}

/**
 * One game on a line: icon, name, what kind of game, and Play. The name and
 * icon open the game's page; Play opens the game. A chart passes its rank.
 */
export function GameRow({
  game,
  rank,
  iconSize = 'sm',
  headingLevel = 3,
}: {
  game: GameCard;
  rank?: number;
  iconSize?: GameIconSize;
  headingLevel?: 2 | 3 | 4;
}): ReactElement {
  const { Link, now } = useGamesSite();
  const subtitle = useGameSubtitle(game);
  const Heading = `h${headingLevel}` as const;
  return (
    <div className="fg-row">
      {rank !== undefined && (
        <span className="fg-row__rank" aria-label={`Number ${rank}`}>
          {rank}
        </span>
      )}
      <Link href={gamePagePath(game.slug)} className="fg-row__main">
        <GameIcon game={game} size={iconSize} />
        {/* A div, not a span: it holds a heading. */}
        <div className="fg-row__text">
          <Heading className="fg-row__title">{game.title}</Heading>
          <span className="fg-row__subtitle">
            {isNewGame(game.liveAt, now) && <span className="fg-badge">New</span>}
            {subtitle}
          </span>
        </div>
      </Link>
      <PlayButton game={game} />
    </div>
  );
}

/** One game standing up: a big icon over its name — the shelf card. */
export function GameTile({ game }: { game: GameCard }): ReactElement {
  const { Link } = useGamesSite();
  const subtitle = useGameSubtitle(game);
  return (
    <div className="fg-tile">
      <Link href={gamePagePath(game.slug)} className="fg-tile__main">
        <GameIcon game={game} size="lg" />
        <span className="fg-tile__title">{game.title}</span>
        <span className="fg-tile__subtitle">{subtitle}</span>
      </Link>
      <PlayButton game={game} />
    </div>
  );
}
